import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
} from 'antd';
import {
  AIProvider,
  createProvider,
  ProviderInput,
  providerError,
  updateProvider,
} from './api';

export const providerKinds = [
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'lmstudio', label: 'LM Studio' },
  { value: 'vllm', label: 'vLLM' },
  { value: 'ollama', label: 'Ollama' },
];
const capabilities = [
  { value: 'auto', label: 'Use provider default' },
  { value: 'yes', label: 'Supported' },
  { value: 'no', label: 'Not supported' },
];
type Fields = Omit<
  ProviderInput,
  'extra' | 'supports_json_schema' | 'supports_images'
> & {
  extra: string;
  supports_json_schema: string;
  supports_images: string;
};
const capability = (value: boolean | null | undefined) =>
  value == null ? 'auto' : value ? 'yes' : 'no';
const parseExtra = (text: string) => {
  const result = JSON.parse(text || '{}');
  if (!result || Array.isArray(result) || typeof result !== 'object')
    throw new Error('Enter a JSON object.');
  return result;
};

const ProviderEditor: React.FC<{
  provider: AIProvider | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ provider, onClose, onSaved }) => {
  const [form] = Form.useForm<Fields>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async (values: Fields) => {
    setSaving(true);
    setError('');
    try {
      const { api_key, extra, supports_json_schema, supports_images, ...rest } =
        values;
      const payload: ProviderInput = {
        ...rest,
        name: rest.name.trim(),
        model: rest.model.trim(),
        base_url: rest.base_url.trim().replace(/\/+$/, ''),
        context_window: rest.context_window || null,
        max_output_tokens: rest.max_output_tokens || null,
        timeout_seconds: rest.timeout_seconds || null,
        supports_json_schema:
          supports_json_schema === 'auto'
            ? null
            : supports_json_schema === 'yes',
        supports_images:
          supports_images === 'auto' ? null : supports_images === 'yes',
        extra: parseExtra(extra),
        ...(api_key?.trim() ? { api_key: api_key.trim() } : {}),
      };
      if (provider) await updateProvider(provider.id, payload);
      else await createProvider(payload);
      form.resetFields();
      onSaved();
    } catch (reason) {
      setError(providerError(reason));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Drawer
      open
      title={provider ? `Edit ${provider.name}` : 'Add AI provider'}
      onClose={saving ? undefined : onClose}
      closable={!saving}
      mask={{ closable: !saving }}
      keyboard={!saving}
      size={600}
      extra={
        <Space>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            Save provider
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          type="error"
          showIcon
          title="Provider could not be saved"
          description={error}
          style={{ marginBottom: 20 }}
        />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={saving}
        onFinish={save}
        initialValues={{
          kind: 'openrouter',
          name: '',
          model: '',
          base_url: '',
          enabled: true,
          bulk_only: false,
          priority: 100,
          thinking_enabled: false,
          sampling_profile: '',
          context_window: null,
          max_output_tokens: null,
          timeout_seconds: null,
          ...provider,
          api_key: '',
          extra: JSON.stringify(provider?.extra || {}, null, 2),
          supports_json_schema: capability(provider?.supports_json_schema),
          supports_images: capability(provider?.supports_images),
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[{ required: true, whitespace: true, max: 64 }]}
            >
              <Input placeholder="e.g. openrouter-glm" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="kind"
              label="Provider type"
              rules={[{ required: true }]}
            >
              <Select options={providerKinds} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="model"
          label="Model"
          tooltip="Use the exact model identifier served by this endpoint."
          rules={[{ required: true, whitespace: true, max: 200 }]}
        >
          <Input placeholder="Model identifier" />
        </Form.Item>
        <Form.Item
          name="base_url"
          label="API base URL"
          tooltip="The API endpoint, usually ending in /v1. Local addresses must be reachable from the worker container."
          rules={[
            { required: true, whitespace: true, max: 300 },
            {
              validator: async (_, value) => {
                if (!value) return;
                try {
                  const url = new URL(value);
                  if (
                    !['https:', 'http:'].includes(url.protocol) ||
                    url.username ||
                    url.password
                  )
                    throw new Error();
                } catch {
                  throw new Error(
                    'Enter an HTTP or HTTPS URL without embedded credentials.',
                  );
                }
              },
            },
          ]}
        >
          <Input placeholder="https://provider.example/api/v1" />
        </Form.Item>
        <Form.Item
          name="api_key"
          label="API key"
          extra={
            provider?.has_api_key
              ? 'A key is stored. Leave blank to keep it, or enter a replacement.'
              : 'Optional for local providers. Stored keys are never displayed.'
          }
          rules={[{ max: 300 }]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={
              provider?.has_api_key ? 'Keep existing key' : 'Enter API key'
            }
          />
        </Form.Item>
        <Form.Item
          name="enabled"
          label="Enabled"
          valuePropName="checked"
          tooltip={
            provider?.is_active
              ? 'Choose another primary provider before disabling this one.'
              : 'Enabled providers can serve as fallbacks when the primary is unavailable.'
          }
        >
          <Switch disabled={Boolean(provider?.is_active)} />
        </Form.Item>
        <Form.Item
          name="bulk_only"
          valuePropName="checked"
          extra={
            provider?.is_active
              ? 'Choose another primary provider before reserving this provider for bulk work.'
              : 'Used only when explicitly selected for bulk corpus jobs. Excluded from interactive requests and automatic fallback.'
          }
        >
          <Checkbox disabled={Boolean(provider?.is_active)}>
            Dedicated bulk provider
          </Checkbox>
        </Form.Item>
        <Collapse
          ghost
          items={[
            {
              key: 'advanced',
              label: 'Advanced settings',
              forceRender: true,
              children: (
                <>
                  <Form.Item
                    name="priority"
                    label="Fallback priority"
                    tooltip="Lower values are tried first. A feature that requests a specific model only uses matching providers."
                    rules={[{ required: true }]}
                  >
                    <InputNumber precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="context_window"
                        label="Context window"
                        tooltip="Total token capacity. Leave empty to use the provider default."
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          placeholder="Provider default"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="max_output_tokens"
                        label="Output token limit"
                        tooltip="Includes reasoning tokens for hosted models. Leave empty to use the provider default."
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          placeholder="Provider default"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="timeout_seconds"
                    label="Request timeout (seconds)"
                  >
                    <InputNumber
                      min={1}
                      placeholder="Provider default"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="supports_json_schema"
                        label="Native JSON schema"
                      >
                        <Select options={capabilities} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item name="supports_images" label="Image input">
                        <Select options={capabilities} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    name="thinking_enabled"
                    label="Allow reasoning"
                    valuePropName="checked"
                    tooltip="Turning this off requests suppression on local engines. Hosted providers may require reasoning regardless of this setting."
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item name="sampling_profile" label="Sampling profile">
                    <Select
                      options={[
                        { value: '', label: 'Use provider default' },
                        { value: 'local_qwen', label: 'Local engine' },
                        { value: 'hosted_default', label: 'Hosted API' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name="extra"
                    label="Additional configuration (JSON)"
                    tooltip="Optional headers, extra_body, and sampling overrides."
                    rules={[
                      {
                        validator: async (_, value) => {
                          try {
                            parseExtra(value);
                          } catch {
                            throw new Error('Enter a valid JSON object.');
                          }
                        },
                      },
                    ]}
                  >
                    <Input.TextArea
                      autoSize={{ minRows: 4, maxRows: 12 }}
                      spellCheck={false}
                    />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};
export default ProviderEditor;
